import React, { useEffect, useState } from "react";
import {
  Button,
  Typography,
  Container,
  Paper,
  CircularProgress,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import Navbar from "../../../globals/components/Navbar";
import { useNavigate } from "react-router";
import { apiClient } from "../../../api/httpClient";

const START_REQUEST_CONFIG = {
  creation: {
    title: "Initiate Bond Request",
    floridaOnlyStates: true,
    routes: {
      Probate: "/vermost-probate-bond-request",
      Blanket: "/vermost-blanket-bond-request",
      Guardianship: "/vermost-guardianship-bond-request",
      VA: "/vermost-va-bond-request",
      Trustee: "/vermost-trustee-bond-request",
    },
  },
  historic: {
    title: "Initiate Historic Bond Request",
    floridaOnlyStates: false,
    routes: {
      Probate: "/vermost-probate-bond-historic",
      Guardianship: "/vermost-guardianship-bond-historic",
      VA: "/vermost-va-bond-historic",
      Trustee: "/vermost-trustee-bond-historic",
      Blanket: "/vermost-blanket-bond-historic",
    },
  },
};

const StartRequestBase = ({ variant }) => {
  const config = START_REQUEST_CONFIG[variant];
  const navigate = useNavigate();
  const { handleSubmit, control, setValue } = useForm();
  const [bondTypes, setBondTypes] = useState([]);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchBondTypes = async () => {
      try {
        const response = await apiClient("/bondtypes/", "GET");
        const list = Array.isArray(response) ? response : response?.data || [];
        const filteredBondTypes = list.filter(
          (bondType) => bondType.type !== "Guardian-Minor"
        );
        const formattedBondTypes = filteredBondTypes.map((item) => ({
          value: item.type,
          label: item.type,
        }));
        setBondTypes(formattedBondTypes);

        const defaultBondType = formattedBondTypes.find(
          (item) => item.label === "Probate"
        );
        if (defaultBondType) {
          setValue("bondType", defaultBondType);
        }
      } catch (error) {
        console.error("Error fetching bond types:", error);
      }
    };

    const fetchStates = async () => {
      try {
        const response = await apiClient("/states/0/", "GET");
        const list = Array.isArray(response) ? response : response?.data || [];
        const formattedStates = list
          .map((item) => ({
            value: item.state_code,
            label: item.state_name,
            id: item.bond_state_id,
          }))
          .filter(
            (item) => !config.floridaOnlyStates || item.label === "Florida"
          );

        setStates(formattedStates);

        const defaultState = formattedStates.find(
          (item) => item.label === "Florida"
        );
        if (defaultState) {
          setValue("state", defaultState);
        }
      } catch (error) {
        console.error("Error fetching states:", error);
      }
    };

    setLoading(true);
    Promise.all([fetchBondTypes(), fetchStates()]).finally(() =>
      setLoading(false)
    );
  }, [setValue, config.floridaOnlyStates]);

  const onSubmit = (data) => {
    const { state, bondType } = data;

    localStorage.setItem("selectedState", JSON.stringify(state));
    localStorage.setItem("selectedBondType", JSON.stringify(bondType));

    const route = config.routes[bondType?.label];
    if (route) {
      navigate(route);
    } else {
      console.error("Unknown bond type:", bondType);
    }
  };

  return (
    <>
      <Navbar />
      <Container maxWidth="sm" style={{ marginTop: "3rem" }}>
        <Paper
          data-testid="bond-create-start-page"
          elevation={3}
          style={{
            padding: "2rem",
            backgroundColor: "#f5f5f5",
            borderRadius: "15px",
          }}
        >
          <Typography
            variant="h5"
            gutterBottom
            style={{ textAlign: "center", fontWeight: "bold" }}
          >
            {config.title}
          </Typography>
          {loading ? (
            <CircularProgress style={{ display: "block", margin: "0 auto" }} />
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <Controller
                    name="state"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <Select
                        {...field}
                        inputId="bond-create-state"
                        options={states}
                        placeholder="Select a State"
                        onChange={(value) => setValue("state", value)}
                        isSearchable
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Controller
                    name="bondType"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <Select
                        {...field}
                        inputId="bond-create-bond-type"
                        options={bondTypes}
                        placeholder="Select a Bond Type"
                        onChange={(value) => setValue("bondType", value)}
                        isSearchable
                      />
                    )}
                  />
                </Grid>

                <Grid
                  size={{ xs: 12 }}
                  style={{ textAlign: "center", marginTop: "1rem" }}
                >
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    data-testid="bond-create-start-submit"
                  >
                    Start Request
                  </Button>
                </Grid>
              </Grid>
            </form>
          )}
        </Paper>
      </Container>
    </>
  );
};

export default StartRequestBase;
